import { useState, type ReactNode } from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { getSuggestedTimes } from "@/lib/postingSchedule"
import { CalendarClock, Clock, Star, X } from "lucide-react"
import { de } from "date-fns/locale"

interface SchedulePopoverProps {
  platform: string
  platformName: string
  /** Already-scheduled times for this platform (conflict detection). */
  scheduledTimes: Date[]
  /** Receives the final Date (Berlin-adjusted for custom times). */
  onSchedule: (date: Date) => void
  isPending: boolean
  /** The trigger button. */
  children: ReactNode
}

// Extracted from NewsPostsModal, where this UI existed twice (~100 lines each: "Planen"
// and "Neu planen") sharing ONE open/date state set across every post and platform tab.
// Each instance now owns its state. The popover closes on selection; errors surface via
// the parent mutation's toast and the user can simply reopen.
export function SchedulePopover({
  platform,
  platformName,
  scheduledTimes,
  onSchedule,
  isPending,
  children,
}: SchedulePopoverProps) {
  const [open, setOpen] = useState(false)
  const [customTimeOpen, setCustomTimeOpen] = useState(false)
  const [customDate, setCustomDate] = useState<Date | undefined>(undefined)
  const [customHour, setCustomHour] = useState("12")
  const [customMinute, setCustomMinute] = useState("00")

  const pick = (date: Date) => {
    setOpen(false)
    setCustomTimeOpen(false)
    onSchedule(date)
  }

  const handleCustomSchedule = () => {
    if (!customDate) return
    const scheduled = new Date(customDate)
    scheduled.setHours(parseInt(customHour, 10), parseInt(customMinute, 10), 0, 0)
    // The picked wall-clock time is meant as Berlin time regardless of the user's device zone
    const berlinStr = scheduled.toLocaleString("en-US", { timeZone: "Europe/Berlin" })
    const berlinLocal = new Date(berlinStr)
    const offset = scheduled.getTime() - berlinLocal.getTime()
    pick(new Date(scheduled.getTime() + offset))
  }

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        if (!o) setCustomTimeOpen(false)
      }}
    >
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        {!customTimeOpen ? (
          <div className="p-3 space-y-1">
            <p className="text-sm font-medium text-muted-foreground px-2 pb-2">
              Optimale Zeiten ({platformName})
            </p>
            {getSuggestedTimes(platform as any, scheduledTimes).map((slot, i) => (
              <button
                key={i}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors text-left ${slot.isTaken ? "opacity-40 cursor-not-allowed" : "hover:bg-accent"}`}
                onClick={() => !slot.isTaken && pick(slot.date)}
                disabled={isPending || slot.isTaken}
              >
                <div className="flex items-center gap-2">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className={slot.isTaken ? "line-through" : ""}>{slot.label}</span>
                </div>
                <div className="flex items-center gap-1">
                  {slot.isTaken && <span className="text-xs text-muted-foreground">belegt</span>}
                  {slot.isPeak && !slot.isTaken && (
                    <Badge variant="secondary" className="text-xs gap-1 py-0">
                      <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                      Peak
                    </Badge>
                  )}
                </div>
              </button>
            ))}
            <div className="border-t mt-2 pt-2">
              <button
                className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-accent transition-colors text-left text-muted-foreground"
                onClick={() => setCustomTimeOpen(true)}
              >
                <CalendarClock className="h-3.5 w-3.5" />
                Eigene Zeit wählen
              </button>
            </div>
          </div>
        ) : (
          <div className="p-3 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Eigene Zeit wählen</p>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2"
                onClick={() => setCustomTimeOpen(false)}
                aria-label="Eigene Zeit schließen"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
            <Calendar
              mode="single"
              selected={customDate}
              onSelect={setCustomDate}
              locale={de}
              disabled={(date) => {
                const today = new Date()
                today.setHours(0, 0, 0, 0)
                return date < today || date.getDay() === 0 || date.getDay() === 6
              }}
              className="rounded-md border"
            />
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground whitespace-nowrap">Uhrzeit:</Label>
              <select
                value={customHour}
                onChange={(e) => setCustomHour(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                aria-label="Stunde"
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={String(i).padStart(2, "0")}>
                    {String(i).padStart(2, "0")}
                  </option>
                ))}
              </select>
              <span className="text-muted-foreground">:</span>
              <select
                value={customMinute}
                onChange={(e) => setCustomMinute(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                aria-label="Minute"
              >
                {["00", "15", "30", "45"].map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <Button
              size="sm"
              className="w-full"
              onClick={handleCustomSchedule}
              disabled={!customDate || isPending}
            >
              <CalendarClock className="h-4 w-4 mr-2" />
              Planen
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
